// ============================================================
// Profile.jsx — Panel lateral del Navbar
// ============================================================
// FIXES aplicados:
// ✅ Flecha de cierre en la parte superior
// ✅ Portada cargada en paralelo con tienda
// ✅ Cache de imágenes: se pre-cargan al iniciar sesión o al montar
//    el componente con cliente ya logueado — quedan listas al abrir
// ✅ [NUEVO] Sistema de pestañas: Pedidos / Descargas
// ✅ [NUEVO] Componente TabDescargas integrado
// ============================================================

import React, { useEffect, useState, useRef } from 'react';
import logo from '../../images/logo.png';
import './Profile.css';
import { Link as Anchor } from 'react-router-dom';
import baseURL from '../url';
import ShareWeb from '../ShareWeb/ShareWeb';
import moneda from '../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingBag, faBoxOpen, faSignOutAlt, faUser,
    faChevronDown, faChevronUp, faSpinner, faMapMarkerAlt,
    faChevronLeft, faDownload, faLock
} from '@fortawesome/free-solid-svg-icons';

// [NUEVO] Importar el componente de pestaña de descargas
import TabDescargas from './TabDescargas';

// ------------------------------------------------------------------
// Cache global de imágenes por idProducto
// Vive mientras la página esté abierta
// Estructura: { [idProducto]: 'url_imagen' }
// ------------------------------------------------------------------
const imageCache = {};

// Pide la imagen de UN producto y la guarda en el cache
async function precargarImagen(idProducto) {
    if (!idProducto || imageCache[idProducto] !== undefined) return;
    imageCache[idProducto] = ''; // marca "en proceso" para no pedir dos veces
    try {
        const res  = await fetch(`${baseURL}/productoGet.php?idProducto=${idProducto}`);
        const data = await res.json();
        imageCache[idProducto] = data.producto?.imagen1 || data.producto?.imagen2 || '';
    } catch {
        imageCache[idProducto] = '';
    }
}

// Recorre todos los pedidos, extrae idProducto únicos y los pre-carga en paralelo
async function precargarImagenesDePedidos(pedidos) {
    const ids = new Set();
    pedidos.forEach(pedido => {
        try {
            JSON.parse(pedido.productos || '[]').forEach(prod => {
                if (prod.idProducto) ids.add(prod.idProducto);
            });
        } catch {}
    });
    await Promise.all([...ids].map(id => precargarImagen(id)));
}

// ------------------------------------------------------------------
// Componente auxiliar: ProductoImagen
// Lee del cache — si ya está pre-cargada se muestra al instante.
// Si por algún motivo no estaba, hace fetch puntual como fallback.
// ------------------------------------------------------------------
function ProductoImagen({ idProducto, titulo }) {
    const [src, setSrc] = useState(() => imageCache[idProducto] || '');

    useEffect(() => {
        if (src) return;
        precargarImagen(idProducto).then(() => {
            setSrc(imageCache[idProducto] || '');
        });
    }, [idProducto]);

    return src ? <img src={src} alt={titulo} loading="lazy" /> : null;
}

// ------------------------------------------------------------------
// Componente principal: Profile
// Props:
//   onClose — función opcional para cerrar el panel desde el Navbar
// ------------------------------------------------------------------
export default function Profile({ onClose }) {

    const [tienda,          setTienda]          = useState({});
    const [banner,          setBanner]          = useState('');
    const [cliente,         setCliente]         = useState(() => {
        const saved = localStorage.getItem('cliente');
        return saved ? JSON.parse(saved) : null;
    });
    const [pedidos,         setPedidos]         = useState([]);
    const [cargandoPedidos, setCargandoPedidos] = useState(false);
    const [errorPedidos,    setErrorPedidos]    = useState('');

    // [NUEVO] Estado para controlar la pestaña activa ('pedidos' o 'descargas')
    const [tabActiva, setTabActiva] = useState('pedidos');

    // Para no pre-cargar dos veces en la misma sesión
    const yaPrecargado = useRef(false);

    // ── Al montar: carga tienda + banner en paralelo ──────────────
    useEffect(() => {
        Promise.all([
            fetch(`${baseURL}/tiendaGet.php`).then(r => r.json()),
            fetch(`${baseURL}/bannersGet.php`).then(r => r.json()),
        ]).then(([tiendaData, bannerData]) => {
            setTienda(tiendaData.tienda?.[0] || {});
            const primero = (bannerData.banner || bannerData.banners)?.[0]?.imagen || '';
            setBanner(primero);
        }).catch(err => console.error('Error al cargar tienda/banner:', err));
    }, []);

    // ── Si el cliente ya estaba logueado al montar, pre-carga ─────
    useEffect(() => {
        if (cliente?.idCliente && !yaPrecargado.current) {
            yaPrecargado.current = true;
            cargarYPrecargar(cliente.idCliente);
        }
    }, []);

    // ── [NUEVO] Cuando se activa la pestaña de pedidos, carga si aún no hay datos ──
    useEffect(() => {
        if (tabActiva === 'pedidos' && cliente?.idCliente && pedidos.length === 0 && !cargandoPedidos) {
            cargarYPrecargar(cliente.idCliente);
        }
    }, [tabActiva]); // se dispara al cambiar de pestaña

    // ── Carga pedidos y luego pre-carga imágenes en paralelo ──────
    const cargarYPrecargar = (idCliente) => {
        setCargandoPedidos(true);
        setErrorPedidos('');
        fetch(`${baseURL}/clientePedidosGet.php?idCliente=${idCliente}`)
            .then(r => r.json())
            .then(data => {
                if (data.error) {
                    setErrorPedidos(data.error);
                } else {
                    const lista = data.pedidos || [];
                    setPedidos(lista);
                    // Pre-carga todas las imágenes en segundo plano
                    precargarImagenesDePedidos(lista);
                }
            })
            .catch(() => setErrorPedidos('Error al cargar pedidos.'))
            .finally(() => setCargandoPedidos(false));
    };

    const handleLogout = () => {
        localStorage.removeItem('cliente');
        setCliente(null);
        setPedidos([]);
        yaPrecargado.current = false;
    };

    const formatearFecha = (fechaStr) =>
        new Date(fechaStr).toLocaleString('es-ES', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    const colorEstado = (estado) => {
        switch (estado) {
            case 'Pendiente':   return '#f39c12';
            case 'Preparacion': return '#3498db';
            case 'Terminado':   return '#9b59b6';
            case 'Entregado':   return '#27ae60';
            case 'Rechazado':   return '#e74c3c';
            default:            return '#999';
        }
    };

    return (
        <div className='profileContain'>

            {/* Flecha de cierre */}
            {onClose && (
                <button className="profile-close-btn" onClick={onClose} aria-label="Cerrar panel">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            )}

            {/* Portada + Avatar */}
            <div className="profile-cover-wrap">
                <div
                    className="profile-cover"
                    style={{ backgroundImage: banner ? `url(${banner})` : 'none' }}
                />
                <div className="profile-avatar-center">
                    {cliente ? (
                        cliente.foto ? (
                            <img src={cliente.foto} alt="foto perfil" />
                        ) : (
                            <div className="profile-cliente-avatar">
                                {cliente.nombre?.charAt(0).toUpperCase()}
                            </div>
                        )
                    ) : (
                        <img src={tienda?.imagen1 || logo} alt="logo" />
                    )}
                </div>
            </div>

            {/* Nombre */}
            {cliente ? (
                <div className="profile-nombre-wrap">
                    <h2>{cliente.nombre}</h2>
                    <p className="profile-cliente-email">{cliente.email}</p>
                </div>
            ) : (
                <div className="profile-nombre-wrap">
                    <h2>{tienda.nombre || 'Mi Tienda'}</h2>
                </div>
            )}

            {/* Contenido según autenticación */}
            {cliente ? (
                <div className="profile-cliente-logueado">
                    
                    {/* [NUEVO] Barra de pestañas: Pedidos / Descargas */}
                    <div className="profile-tabs">
                        <button
                            className={`profile-tab-btn ${tabActiva === 'pedidos' ? 'active' : ''}`}
                            onClick={() => setTabActiva('pedidos')}
                        >
                            <FontAwesomeIcon icon={faBoxOpen} /> Mis pedidos
                        </button>
                        <button
                            className={`profile-tab-btn ${tabActiva === 'descargas' ? 'active' : ''}`}
                            onClick={() => setTabActiva('descargas')}
                        >
                            <FontAwesomeIcon icon={faDownload} /> Mis descargas
                        </button>
                    </div>

                    {/* Contenido de la pestaña activa */}
                    {tabActiva === 'pedidos' && (
                        <div className="profile-pedidos-lista">
                            {cargandoPedidos && (
                                <p className="profile-pedidos-loading">
                                    <FontAwesomeIcon icon={faSpinner} spin /> Cargando pedidos...
                                </p>
                            )}
                            {errorPedidos && (
                                <p className="profile-pedidos-error">{errorPedidos}</p>
                            )}
                            {!cargandoPedidos && !errorPedidos && pedidos.length === 0 && (
                                <p className="profile-pedidos-vacio">Aún no tienes pedidos.</p>
                            )}
                            {pedidos.map(pedido => (
                                <div key={pedido.idPedido} className="profile-pedido-card">
                                    <div className="profile-pedido-header">
                                        <span className="profile-pedido-id">Pedido #{pedido.idPedido}</span>
                                        <span
                                            className="profile-pedido-estado"
                                            style={{ backgroundColor: colorEstado(pedido.estado) }}
                                        >
                                            {pedido.estado}
                                        </span>
                                    </div>
                                    <p className="profile-pedido-fecha">{formatearFecha(pedido.createdAt)}</p>
                                    <p className="profile-pedido-entrega">📦 {pedido.entrega}</p>
                                    <div className="profile-pedido-productos">
                                        {JSON.parse(pedido.productos || '[]').map((prod, i) => (
                                            <div key={i} className="profile-pedido-producto">
                                                <ProductoImagen
                                                    idProducto={prod.idProducto}
                                                    titulo={prod.titulo}
                                                />
                                                <div>
                                                    <p>{prod.titulo}</p>
                                                    <span>{moneda} {prod.precio} × {prod.cantidad}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="profile-pedido-total">
                                        Total: <strong>{moneda} {pedido.total?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</strong>
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}

                    {tabActiva === 'descargas' && (
                        // [NUEVO] Renderiza el componente externo TabDescargas
                        // Le pasamos el id del cliente y la baseURL para que obtenga los archivos
                        <TabDescargas
                            idCliente={cliente.idCliente}
                            baseURL={baseURL}
                        />
                    )}

                    {/* Botón de cerrar sesión (fuera de las pestañas, siempre visible) */}
                    <button className="profile-cliente-logout" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} /> Cerrar sesión
                    </button>

                </div>
            ) : (
                <div className="profile-cliente-guest">
                    <FontAwesomeIcon icon={faUser} className="profile-guest-icon" />
                    <p>¿Tienes cuenta? Inicia sesión para ver tus pedidos.</p>
                </div>
            )}

            {/* Info pública de la tienda */}
            <div className='profileText'>
                <Anchor to="/productos">
                    <FontAwesomeIcon icon={faShoppingBag} />
                    <span>Todos los productos</span>
                </Anchor>

                {tienda.direccion && (
                    <Anchor
                        to={`http://maps.google.com/?q=${encodeURIComponent(tienda.direccion)}`}
                        target="_blank" rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>{tienda.direccion}</span>
                    </Anchor>
                )}

                <div className='socials'>
                    <Anchor to={tienda.instagram} target="_blank"><i className='fa fa-instagram'></i></Anchor>
                    <Anchor to={`https://wa.me/${tienda.telefono}`} target="_blank"><i className='fa fa-whatsapp'></i></Anchor>
                    <Anchor to={tienda.facebook} target="_blank"><i className='fa fa-facebook'></i></Anchor>
                </div>
            </div>

            <ShareWeb />
        </div>
    );
}